import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ComposedChart, Area, Bar, Cell, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceDot, ReferenceArea, Customized } from "recharts";
import dayjs from "dayjs";
import { toBandSeries, humanEUR } from "../cost-control/forecast/format";
import { singleSeriesMeta, endLabel } from "../cost-control/forecast/chartAnnotations";
import { mergeForecastRows, foldForecastGroups, forecastKey } from "./forecastBreakdown";
import { accumulateSingle, accumulateStacked, accumulateSeries, accumulateMeasures } from "./cumulative";
import { normalizeDay, bucketSingle, bucketStacked, bucketMeasures, completeMeta, completeStackedMeta, chartTitleFor, toBarSegments, intervalNoun, bucketTickLabel, bucketRangeLabel, bucketEnd } from "./bucketing";
import IntervalSelect from "./IntervalSelect";
import CumulativeToggle from "./CumulativeToggle";

// Re-exported so existing importers (and tests) keep their entry point while the
// derivation itself is shared with the forecast-predictability chart.
export { singleSeriesMeta };

const CHART = "hsl(var(--chart-1))";
const BAND = "hsl(var(--chart-axis))"; // neutral grey-blue for the forecast range (not the accent)
const REF = "hsl(var(--chart-axis))";
const GHOST = "hsl(var(--chart-axis))"; // dashed comparison (prior period / prior year) ghost line
const SHADOW = "hsl(var(--chart-teal))"; // dashed API-equivalent reference line — distinct teal so it never reads as the grey comparison ghost
const TOTAL_LINE = "hsl(var(--text))"; // thin neutral line tracing the top of the stack
// Few "Light" categorical palette — distinct, softer CFO-grade hues. Grey (140,140,140)
// is held back for the "Other" remainder so real categories never read as miscellaneous.
// Exported so the AI Trends chart stacks in the same categorical colours —
// the two analysis pages are meant to read as one system.
export const STACK_PALETTE = ["#88BDE6", "#FBB258", "#90CD97", "#F6AAC9", "#BFA554", "#BC99C7", "#EDDD46", "#F07E6E"];
const STACK_CAP = 5; // top-N distinct layers before the rest fold into "Other"
const OTHER_COLOR = "#8C8C8C"; // light grey for the folded remainder

// normalizeDay now lives in bucketing.js (the bucketing functions need it, and this
// module imports them — the other direction would be a cycle). Re-exported so existing
// importers and tests keep their entry point.
export { normalizeDay };

// attachShadow overlays a per-day API-equivalent (shadow) value as `__shadow` on
// each chart row, matched by the row's x key (date/time). Returns rows unchanged
// when there's no shadow to show, so the overlay disappears.
export function attachShadow(rows, xKey, shadow = []) {
  if (!shadow || !shadow.length) return rows;
  const byTime = {};
  for (const s of shadow) byTime[normalizeDay(s.time)] = Number(s.measure) || 0;
  return rows.map((r) => {
    const v = byTime[normalizeDay(r[xKey])];
    return v == null ? r : { ...r, __shadow: v };
  });
}

const fmtDay = (v) => (dayjs(v).isValid() ? dayjs(v).format("MMM D") : v);

// topGroups ranks grouping values by total |measure| and caps the count so a stacked
// chart never renders an unreadable number of series; everything past the cap folds
// into "Other" in pivotStacked.
export function topGroups(rows, cap = 10) {
  const totals = {};
  for (const r of rows) totals[r.grouping ?? ""] = (totals[r.grouping ?? ""] || 0) + (Number(r.measure) || 0);
  return Object.entries(totals).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).slice(0, cap).map(([g]) => g);
}

// pivotStacked turns long { time, grouping, measure } rows into wide per-time rows keyed
// by group name, for Recharts' stacked Area series. Each present day is DENSIFIED: every
// layer (top groups + "Other") gets an explicit 0 so a source that simply had no cost that
// day reads as a real €0 in the stack — not a null that Recharts renders as a white gap.
export function pivotStacked(rows, groups) {
  const allKeys = [...groups, "Other"];
  const byTime = {};
  const set = new Set(groups);
  for (const r of rows) {
    const t = normalizeDay(r.time);
    if (!byTime[t]) { byTime[t] = { time: t }; for (const k of allKeys) byTime[t][k] = 0; }
    const rg = r.grouping ?? "";
    const g = set.has(rg) ? rg : "Other";
    byTime[t][g] += (Number(r.measure) || 0);
  }
  return Object.values(byTime).sort((a, b) => String(a.time).localeCompare(String(b.time)));
}

// mergeSingle builds the single-series line, CLIPPED to the selected period [from, to]
// (ISO "YYYY-MM-DD"; both optional — omit to disable clipping). When a forecast is present
// the line is the forecast series (actual-to-date + forward forecast/band) on one cost
// basis, so it stays continuous; but only its points inside the period are kept, so a
// "This month" view no longer bleeds into the previous month. Period days that fall BEFORE
// the forecast's trailing window (e.g. the first days of a fully-past month) are backfilled
// from the period spend (spendDetails, now the AI-inclusive spend-series). Without a
// forecast the line is the period spend alone.
export function mergeSingle(spendDetails, forecast, from, to) {
  const fc = forecast || [];
  const hasForecast = fc.some((f) => f.forecast != null);
  // The forecast is clipped to the selected period exactly like the actuals: the
  // chart never projects past `to`. That keeps the last forecast point equal to
  // the projected period-end figure the KPI card states, so the two reconcile by
  // eye. On a to-date period (`to` is today) there is then no room for a forward
  // projection at all — the chart says so rather than drawing an invisible
  // one-point line (see forecastBeyondPeriod in AnalyzerChart).
  const inRange = (t) => (!from || t >= from) && (!to || t <= to);
  const fcInRange = inRange;
  const byDate = {};

  if (hasForecast) {
    let firstFcDate = null;
    for (const f of fc) {
      const t = normalizeDay(f.date);
      if (!fcInRange(t)) continue;
      if (firstFcDate == null || t < firstFcDate) firstFcDate = t;
      byDate[t] = byDate[t] || { date: t };
      if (f.actual != null) byDate[t].actual = f.actual;
      if (f.forecast != null) byDate[t].forecast = f.forecast;
      if (f.lower != null) byDate[t].lower = f.lower;
      if (f.upper != null) byDate[t].upper = f.upper;
    }
    // Backfill period days before the forecast's trailing window from the period spend
    // (don't override the forecast series' own actuals, so the recent window stays one source).
    for (const r of spendDetails) {
      const t = normalizeDay(r.time);
      if (!inRange(t) || (firstFcDate != null && t >= firstFcDate)) continue;
      byDate[t] = byDate[t] || { date: t };
      byDate[t].actual = (byDate[t].actual || 0) + (Number(r.measure) || 0);
    }
    return toBandSeries(Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)));
  }

  for (const r of spendDetails) {
    const t = normalizeDay(r.time);
    if (!inRange(t)) continue;
    byDate[t] = byDate[t] || { date: t };
    byDate[t].actual = (byDate[t].actual || 0) + (Number(r.measure) || 0);
  }
  return toBandSeries(Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)));
}

// stackedMeta derives the diagnostic references for the stacked view: the per-day total
// series average (a run-rate reference), the peak-total day and its dominant driver, and
// the last row (for right-edge layer labels).
export function stackedMeta(data, stackKeys) {
  if (!data || !data.length) return { firstTime: null, lastTime: null, avgTotal: null, peak: null, lastRow: null };
  let sum = 0;
  let peak = null;
  for (const r of data) {
    const total = stackKeys.reduce((s, k) => s + (Number(r[k]) || 0), 0);
    sum += total;
    if (peak == null || total > peak.total) {
      let driver = null, dv = -Infinity;
      for (const k of stackKeys) { const v = Number(r[k]) || 0; if (v > dv) { dv = v; driver = k; } }
      peak = { time: r.time, total, driver };
    }
  }
  return {
    firstTime: data[0].time,
    lastTime: data[data.length - 1].time,
    avgTotal: sum / data.length,
    peak,
    lastRow: data[data.length - 1],
  };
}

// splitIncompleteTail returns the index of the first row in the trailing run of INCOMPLETE
// days — days that are `today` (not yet fully materialised) or whose whole-stack total is
// ~0 (no cost landed yet). Only a TRAILING run counts, so a genuine mid-period zero day is
// untouched. Returns data.length when the tail is complete, and never 0 (we never declare
// the entire series incomplete).
export function splitIncompleteTail(data, stackKeys, today) {
  if (!data || !data.length) return 0;
  const EPS = 1e-9;
  const total = (r) => stackKeys.reduce((s, k) => s + (Number(r[k]) || 0), 0);
  let cut = data.length;
  for (let i = data.length - 1; i >= 1; i--) {
    const incomplete = data[i].time === today || total(data[i]) <= EPS;
    if (incomplete) cut = i; else break;
  }
  return cut;
}

// shortLabel turns a raw billing string into a recognisable chart label: drop any vendor
// path prefix ("anthropic/claude-opus-4-8" → "claude-opus-4-8"), de-hyphenate, title-case
// all-lowercase words, and cap length. The raw value is kept for tooltips/tables.
export function shortLabel(name, max = 18) {
  if (!name) return name;
  let s = String(name);
  if (s.includes("/")) s = s.slice(s.lastIndexOf("/") + 1);
  s = s.replace(/[-_]+/g, " ").trim();
  s = s.split(/\s+/).map((w) => (/^[a-z]+$/.test(w) ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
  if (s.length > max) s = s.slice(0, max - 1).trimEnd() + "…";
  return s;
}

// StackedAnnotations is a Recharts <Customized> child that draws all the on-plot text with
// access to the live axis scales: a right-edge label column (Total spend on top, then each
// visible layer's SHORT name at its band midpoint, de-overlapped), and a structured
// multi-line peak annotation near the peak day. Replaces the detached legend + long raw
// labels so the analyst reads composition directly off the plot.
function StackedAnnotations(props) {
  const { xAxisMap, yAxisMap, offset, stackKeys, colors, lastRow, peak, edgeTime, valueLabel = shortLabel } = props;
  if (!yAxisMap || !offset || !lastRow) return null;
  const yAxis = yAxisMap[Object.keys(yAxisMap)[0]];
  const xAxis = xAxisMap ? xAxisMap[Object.keys(xAxisMap)[0]] : null;
  if (!yAxis || !yAxis.scale) return null;
  const yScale = yAxis.scale;
  // Anchor the right-edge labels to where the stack actually ends (the last complete day),
  // not the plot edge — the incomplete tail is drawn empty beyond it.
  let xRight = offset.left + offset.width;
  if (edgeTime && xAxis && xAxis.scale) {
    const bw = xAxis.scale.bandwidth ? xAxis.scale.bandwidth() : 0;
    const ex = xAxis.scale(edgeTime);
    if (ex != null && !Number.isNaN(ex)) xRight = ex + bw;
  }

  const total = stackKeys.reduce((s, k) => s + (Number(lastRow[k]) || 0), 0);
  const labels = [];
  if (total > 0) labels.push({ key: "__total", text: "Total spend", y: yScale(total), color: TOTAL_LINE, weight: 600 });
  let cum = 0;
  for (let i = 0; i < stackKeys.length; i++) {
    const k = stackKeys[i];
    const v = Number(lastRow[k]) || 0;
    if (v > 0) labels.push({ key: k, text: valueLabel(k), y: yScale(cum + v / 2), color: colors[i], weight: 400 });
    cum += v;
  }
  labels.sort((a, b) => a.y - b.y);
  const gap = 14;
  for (let i = 1; i < labels.length; i++) {
    if (labels[i].y < labels[i - 1].y + gap) labels[i].y = labels[i - 1].y + gap;
  }
  const bottom = offset.top + offset.height;
  for (let i = labels.length - 1; i > 0; i--) {
    if (labels[i].y > bottom) labels[i].y = bottom - (labels.length - 1 - i) * gap;
    if (labels[i - 1].y > labels[i].y - gap) labels[i - 1].y = labels[i].y - gap;
  }

  let peakEls = null;
  if (peak && xAxis && xAxis.scale) {
    const bw = xAxis.scale.bandwidth ? xAxis.scale.bandwidth() : 0;
    const cx = xAxis.scale(peak.time) + bw / 2;
    const cy = yScale(peak.total);
    const anchorRight = cx > offset.left + offset.width * 0.6;
    const tx = anchorRight ? cx - 8 : cx + 8;
    const anchor = anchorRight ? "end" : "start";
    const ty = Math.max(offset.top + 12, cy - 30);
    const dateStr = dayjs(peak.time).isValid() ? dayjs(peak.time).format("MMM D") : peak.time;
    // Short, uncrowded label — the driver + variance detail live in the table below.
    peakEls = (
      <text x={tx} y={ty} textAnchor={anchor} fontSize={11} fill="hsl(var(--text-2))">
        <tspan x={tx} fontWeight={600}>Peak {humanEUR(peak.total)}</tspan>
        <tspan x={tx} dy={14}>{dateStr}</tspan>
      </text>
    );
  }

  return (
    <g>
      {labels.map((l) => (
        <text key={l.key} x={xRight + 6} y={l.y} dy={3} fontSize={11} fontWeight={l.weight} fill={l.color}>{l.text}</text>
      ))}
      {peakEls}
    </g>
  );
}

// StackedContributorsTable is the ranked evidence companion to the stacked chart: exact
// spend, share, and a first-half→second-half trend per source (the chart shows the shape,
// the table supports precise comparison). Raw source names live in the row title attr.
export function StackedContributorsTable({ data, stackKeys, colors, valueLabel = shortLabel }) {
  if (!data || !data.length || !stackKeys || !stackKeys.length) return null;
  const n = data.length;
  const half = Math.floor(n / 2);
  const rows = stackKeys.map((k, i) => {
    let spend = 0, first = 0, second = 0;
    data.forEach((r, idx) => {
      const v = Number(r[k]) || 0; spend += v;
      if (idx < half) first += v; else second += v;
    });
    const firstAvg = half > 0 ? first / half : 0;
    const secondAvg = n - half > 0 ? second / (n - half) : 0;
    const trend = firstAvg > 0 ? (100 * (secondAvg - firstAvg)) / firstAvg : null;
    return { key: k, color: colors[i], spend, trend };
  }).filter((r) => r.spend > 0);
  const total = rows.reduce((s, r) => s + r.spend, 0);
  if (total <= 0) return null;
  return (
    <div className="mt-4">
      <div className="mb-1.5 text-[13px] font-semibold text-text">Top contributors</div>
      <table style={{ width: "100%" }} className="text-[13px]">
        <thead>
          <tr className="text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">
            <th className="py-1.5 text-left">Source</th>
            <th className="text-right">Spend</th>
            <th className="text-right">Share</th>
            <th className="text-right" title="Daily average of the second half of the window vs the first half">Trend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-solid border-card-bd">
              <td className="py-2">
                <span className="inline-flex items-center gap-2" title={r.key}>
                  <span className="inline-block rounded-sm" style={{ width: 9, height: 9, background: r.color }} />
                  <span className="font-medium text-text">{valueLabel(r.key, 28)}</span>
                </span>
              </td>
              <td className="text-right tabular-nums text-text">{humanEUR(r.spend)}</td>
              <td className="text-right tabular-nums text-text-2">{Math.round((100 * r.spend) / total)}%</td>
              <td className="text-right tabular-nums font-medium">
                {r.trend == null
                  ? <span className="text-text-3">—</span>
                  : <span className={r.trend >= 0 ? "text-danger" : "text-success"}>{(r.trend >= 0 ? "+" : "−") + Math.abs(Math.round(r.trend)) + "%"}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// AnalyzerTooltip — single-series reads Actual/Forecast/Range off the hovered row;
// stacked lists whichever group series recharts reports present, plus a bold Total.
export function AnalyzerTooltip({ active, payload, label, hoveredKey, comparisonLabel, valueLabel, cumulative = false, interval = "day" }) {
  if (!active || !payload?.length) return null;
  const dateLabel = bucketRangeLabel(label, interval);
  const hoveredRow = payload[0]?.payload || {};
  const partial = hoveredRow.__partial
    ? `${hoveredRow.__bucketDays} of ${hoveredRow.__calendarDays} days`
    : null;
  const isSingle = payload.some((p) => ["actual", "forecast", "band"].includes(p.dataKey));

  if (isSingle) {
    const row = payload[0]?.payload || {};
    const rows = [];
    if (row.actual != null) rows.push({ key: "actual", label: cumulative ? "Cumulative actual" : "Actual", value: humanEUR(row.actual) });
    if (row.forecast != null) rows.push({ key: "forecast", label: cumulative ? "Cumulative forecast" : "Forecast", value: humanEUR(row.forecast) });
    const band = Array.isArray(row.band) ? row.band : (row.lower != null && row.upper != null ? [row.lower, row.upper] : null);
    if (band) rows.push({ key: "range", label: "Forecast range", value: `${humanEUR(band[0])} – ${humanEUR(band[1])}` });
    if (comparisonLabel && row.__comparison != null) rows.push({ key: "comparison", label: comparisonLabel, value: humanEUR(row.__comparison), muted: true });
    if (rows.length === 0) return null;
    return (
      <div className="rounded-card border border-card-bd bg-card px-3.5 py-3">
        <div className="mb-1.5 text-sm font-semibold text-text-2">{dateLabel}</div>
        {partial && <div className="mb-1 text-[11px] text-text-3">{partial}</div>}
        {rows.map((r) => (
          <div key={r.key} className={`flex items-center justify-between gap-4 text-xs ${r.muted ? "text-text-3" : "text-text-2"}`}>
            <span>{r.label}</span>
            <span className={`tabular-nums ${r.muted ? "text-text-3" : "text-text"}`}>{r.value}</span>
          </div>
        ))}
      </div>
    );
  }

  // __total is the total-line series and __comparison is the ghost overlay — neither is a
  // stacked source, so they must not appear as their own rows (Total is summarised below,
  // the comparison is shown after it).
  const rows = payload
    .filter((p) => p.value != null && p.dataKey && p.dataKey !== "__total" && p.dataKey !== "__comparison")
    .map((p) => ({ key: p.dataKey, label: valueLabel ? valueLabel(p.dataKey) : (p.name ?? p.dataKey), value: Number(p.value) || 0, color: p.color }));
  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + r.value, 0);
  const compVal = payload.find((p) => p.dataKey === "__comparison")?.value;
  return (
    <div className="rounded-card border border-card-bd bg-card px-3.5 py-3">
      <div className="mb-1.5 text-sm font-semibold text-text-2">{dateLabel}</div>
      {partial && <div className="mb-1 text-[11px] text-text-3">{partial}</div>}
      {rows.map((r) => {
        const hovered = r.key === hoveredKey;
        return (
          <div key={r.key} className={`flex items-center justify-between gap-4 text-xs ${hovered ? "font-semibold text-text" : "text-text-2"}`}>
            <span className="flex items-center gap-1.5">
              {r.color && <span className="inline-block shrink-0 rounded-full" style={{ width: 8, height: 8, backgroundColor: r.color }} />}
              {r.label}
            </span>
            <span className="tabular-nums text-text">
              {humanEUR(r.value)}
              {total > 0 && <span className={`ml-1.5 ${hovered ? "text-text-2" : "text-text-3"}`}>{Math.round((100 * r.value) / total)}%</span>}
            </span>
          </div>
        );
      })}
      <div className="mt-1 flex items-center justify-between gap-4 border-t border-card-bd pt-1 text-xs font-semibold text-text">
        <span>Total</span>
        <span className="tabular-nums">{humanEUR(total)}</span>
      </div>
      {comparisonLabel && compVal != null && (
        <div className="mt-0.5 flex items-center justify-between gap-4 text-xs text-text-3">
          <span>{comparisonLabel}</span>
          <span className="tabular-nums">{humanEUR(compVal)}</span>
        </div>
      )}
    </div>
  );
}
AnalyzerTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cumulative: PropTypes.bool,
  interval: PropTypes.oneOf(["day", "week", "month"]),
};

// AnalyzerChart renders either a single Actual+Forecast diagnostic line (isSingleSeries)
// or a stacked breakdown by dimension. Single-series is intentionally an evidence chart:
// solid Actual, dashed Forecast, a light forecast-only range band, a "Forecast starts"
// rule, a daily-average reference line, and a peak annotation.
export default function AnalyzerChart({ isSingleSeries, spendDetails = [], forecast = [], onAddFilter, onZoom, title, groupByLabel = "cost source", from, to, comparison = [], comparisonLabel = "Prior period", valueLabel = shortLabel, shadow = [], forecastBreakdown = null, hasActiveFilters = false, interval = "day", cumulative = false, intervalChoice = "day", onIntervalChange, onCumulativeChange }) {
  // Cumulative is a REPRESENTATION of the same fetched series, and so is the
  // interval: every bucket and running total below is derived from the rows the
  // daily pipeline already built and already clipped, so neither control costs a
  // request. Order matters — SUM into buckets first, THEN accumulate. Accumulating
  // daily and sampling each bucket's last day agrees only by luck of the null
  // pattern, and breaks on any bucket whose final day has no row.
  const isCumulative = cumulative;
  const isMonth = interval === "month";
  const spansYears = !!(from && to) && from.slice(0, 4) !== to.slice(0, 4);
  // Unique per chart instance so two AnalyzerCharts on the same page (e.g. a
  // side-by-side comparison) never collide on the same SVG <pattern> id.
  const hatchId = `analyzer-forecast-hatch-${React.useId().replace(/:/g, "")}`;
  const groups = useMemo(() => (isSingleSeries ? [] : topGroups(spendDetails, STACK_CAP)), [isSingleSeries, spendDetails]);
  const rawData = useMemo(() => (isSingleSeries ? mergeSingle(spendDetails, forecast, from, to) : pivotStacked(spendDetails, groups)), [isSingleSeries, spendDetails, forecast, groups, from, to]);
  const xKey = isSingleSeries ? "date" : "time";

  // Stacked mode: fixed stack order (largest/most-stable at the bottom), a per-row total
  // for the top line + total reference, and the diagnostic references.
  const stackKeys = useMemo(() => (isSingleSeries ? [] : [...groups, "Other"]), [isSingleSeries, groups]);
  const stackColors = useMemo(() => stackKeys.map((g, i) => (g === "Other" ? OTHER_COLOR : STACK_PALETTE[i])), [stackKeys]);
  // Densified rows + a per-row total.
  const stackedBase = useMemo(() => {
    if (isSingleSeries) return [];
    return rawData.map((r) => ({ ...r, __total: stackKeys.reduce((s, k) => s + (Number(r[k]) || 0), 0) }));
  }, [isSingleSeries, rawData, stackKeys]);

  // Trailing incomplete days (today / no-cost-yet): TRIMMED from the chart entirely so the
  // x-axis reserves no empty space for them — they're excluded from the stats too, and the
  // subtitle notes they're still landing.
  const today = dayjs().format("YYYY-MM-DD");
  const cut = useMemo(() => (isSingleSeries ? 0 : splitIncompleteTail(stackedBase, stackKeys, today)), [isSingleSeries, stackedBase, stackKeys, today]);
  const hasIncompleteTail = !isSingleSeries && cut > 0 && cut < stackedBase.length;
  const completeRows = useMemo(() => (hasIncompleteTail ? stackedBase.slice(0, cut) : stackedBase), [hasIncompleteTail, stackedBase, cut]);

  // Forward (forecast) segments stack on their own keys so the projected portion can be
  // drawn in a lighter fill while keeping each group's colour and stack order. The band
  // is total-only — per-segment fans are unreadable past two or three segments. A group
  // that only appears in the forecast (not in the actual top-N) is appended AFTER the
  // actual stack keys, so the existing colour indices for real groups never shift.
  // Capped and Other-folded against the ACTUAL stack, so the forward side never
  // renders more layers than the actual side and a group folded before today is
  // folded after it too (see foldForecastGroups).
  const { groups: fcGroups, names: fcNames } = useMemo(
    () => foldForecastGroups(forecastBreakdown?.groups || [], groups, STACK_CAP),
    [forecastBreakdown, groups]);
  const allStackKeys = useMemo(() => [...stackKeys, ...fcNames.filter((n) => !stackKeys.includes(n))], [stackKeys, fcNames]);
  const allStackColors = useMemo(() => allStackKeys.map((g, i) => (g === "Other" ? OTHER_COLOR : STACK_PALETTE[i % STACK_PALETTE.length])), [allStackKeys]);
  const { rows: mergedRows, forwardFrom: rawForwardFrom } = useMemo(() => {
    if (isSingleSeries || !fcGroups.length) return { rows: completeRows, forwardFrom: null };
    return mergeForecastRows(completeRows, fcGroups, forecastBreakdown?.total || [], allStackKeys, hasActiveFilters);
  }, [isSingleSeries, completeRows, fcGroups, forecastBreakdown, allStackKeys, hasActiveFilters]);
  // mergeForecastRows keys a forward row's day on `bucket` (its own generic day field);
  // the stacked chart's x axis reads `time`, so mirror it across on the forward slice
  // only — the actual rows already carry `time` and are passed through untouched.
  //
  // The forward rows are then clipped at the period end, mirroring the single-series
  // rule above — the forecast breakdown is fetched with a 90-day horizon, of which
  // only the part inside the selection is drawn. `forwardFrom` is recomputed against
  // what SURVIVES the clip: it drives the boundary rule and the forward areas, so
  // leaving it pointing at a day no longer in the data would draw a rule at a
  // category the axis does not have. `forwardCount` is how many forward rows
  // SURVIVE the clip — fewer than two cannot draw a visible area, which is the
  // stacked mirror of the single-series forecastBeyondPeriod note below.
  const { rows: chartRows, forwardFrom, forwardCount } = useMemo(() => {
    if (rawForwardFrom == null) return { rows: mergedRows, forwardFrom: null, forwardCount: 0 };
    const mirrored = mergedRows.map((r) => (r.time == null && r.bucket != null ? { ...r, time: r.bucket } : r));
    const countForward = (rows) => rows.filter((r) => String(r.time) >= rawForwardFrom).length;
    if (!to) return { rows: mirrored, forwardFrom: rawForwardFrom, forwardCount: countForward(mirrored) };
    const clipped = mirrored.filter((r) => String(r.time) <= to);
    const n = countForward(clipped);
    return { rows: clipped, forwardFrom: n > 0 ? rawForwardFrom : null, forwardCount: n };
  }, [mergedRows, rawForwardFrom, to]);
  const bucketKeysForStack = useMemo(
    () => [...allStackKeys, ...allStackKeys.map(forecastKey)],
    [allStackKeys]);
  const bucketed = useMemo(() => {
    const base = isSingleSeries ? rawData : chartRows;
    return isSingleSeries
      ? bucketSingle(base, interval, from, to)
      : bucketStacked(base, bucketKeysForStack, interval, from, to);
  }, [isSingleSeries, rawData, chartRows, interval, from, to, bucketKeysForStack]);
  // `disjoint` tells the accumulators which meaning to give a row that carries BOTH
  // an actual and a forecast. At day granularity the two describe the SAME day (the
  // forecast restates it), so the cumulative forecast anchors on the cumulative
  // actual. Once bucketed, they are sums of DIFFERENT days — bucketSingle already
  // dropped the handover day's forecast — so the forecast adds on top. Passing the
  // wrong one here silently deletes the handover bucket's whole forward half.
  const disjointFields = interval !== "day";
  const renderData = useMemo(() => {
    if (!isCumulative) return bucketed;
    return isSingleSeries
      ? accumulateSingle(bucketed, disjointFields)
      : accumulateStacked(bucketed, stackKeys, allStackKeys, disjointFields);
  }, [isSingleSeries, bucketed, isCumulative, stackKeys, allStackKeys, disjointFields]);
  // meta's diagnostic references describe the BUCKETS the chart actually draws:
  // completeMeta's average and peak are taken over complete buckets only, so a
  // half-landed month never drags the run-rate reference down.
  const meta = useMemo(() => {
    if (!isSingleSeries) return null;
    return { ...singleSeriesMeta(bucketed), ...completeMeta(bucketed) };
  }, [isSingleSeries, bucketed]);
  // ...but the SUBTITLE quotes calendar dates to a reader, and a bucket key is not
  // one: at Month `meta.lastDate` is the month-FIRST of the last bucket, so a chart
  // covering Aug 1 – Sep 30 announced itself as "Aug 1 – Sep 1" and claimed actuals
  // stopped on Sep 1 while the partial-bucket note in the same line said September
  // had 15 days of data. The dates therefore come from the DAY-level rows at every
  // interval; at day granularity bucketing is an identity no-op, so this is the
  // same object the subtitle already read.
  const dayMeta = useMemo(
    () => (isSingleSeries ? singleSeriesMeta(rawData) : null),
    [isSingleSeries, rawData]);

  // Comparison ghost: the comparison period's daily total aligned to the current period by
  // day-offset (day 1 over day 1), injected as a __comparison series so it overlays the
  // actual/forecast (single) or the stack + total (stacked) WITHOUT replacing the chart.
  const compTotals = useMemo(() => {
    if (!comparison || !comparison.length) return [];
    // Bucketed against its OWN dates, then aligned BY INDEX — which is what turns
    // the existing day-1-over-day-1 alignment into bucket-1-over-bucket-1, so
    // "Sep vs Aug" compares whole months rather than offset day windows.
    const bucketedComp = bucketMeasures(comparison, interval);
    const byDay = {};
    for (const r of bucketedComp) { const t = normalizeDay(r.time); byDay[t] = (byDay[t] || 0) + (Number(r.measure) || 0); }
    // Sorted by day before the running sum: accumulation is order-DEPENDENT where the
    // daily view was not, so the curve must never depend on the payload's arrival order.
    const totals = Object.entries(byDay).sort((a, b) => String(a[0]).localeCompare(String(b[0]))).map(([, v]) => v);
    // Already aligned day-1-over-day-1 by offset, so the running total compares
    // elapsed progression: "€165 so far vs €315 by the same point last period".
    return isCumulative ? accumulateSeries(totals) : totals;
  }, [comparison, isCumulative, interval]);
  const hasComparison = compTotals.length > 0;
  // Same rule for the shadow overlay: it is consumed in arrival order, so sort a COPY
  // by day before the running sum (daily mode needs no sort — each point stands alone).
  const shadowRows = useMemo(() => {
    const bucketedShadow = bucketMeasures(shadow || [], interval);
    if (!isCumulative) return bucketedShadow;
    const sorted = [...bucketedShadow].sort((a, b) => String(normalizeDay(a.time)).localeCompare(String(normalizeDay(b.time))));
    return accumulateMeasures(sorted);
  }, [isCumulative, shadow, interval]);
  const data = useMemo(() => {
    const base = !hasComparison ? renderData : renderData.map((r, i) => ({ ...r, __comparison: i < compTotals.length ? compTotals[i] : null }));
    return attachShadow(base, xKey, shadowRows);
  }, [renderData, hasComparison, compTotals, xKey, shadowRows]);
  // Month draws BARS: months are discrete financial periods, and a bar reads as a
  // period total where a line implies a continuous rate. The current month's bar
  // stacks solid actual under hatched forecast, so its height is the projected
  // month total. toBarSegments keeps that honest in both display modes.
  const barData = useMemo(
    () => (isMonth && isSingleSeries ? toBarSegments(data, isCumulative) : data),
    [isMonth, isSingleSeries, data, isCumulative]);
  // Partial buckets are named rather than silently short. Hatching is reserved for
  // "projected", so a partial bar is marked with a dashed outline instead — one
  // texture cannot mean two things, and the current month is usually both.
  const partialNote = useMemo(() => {
    const partials = (bucketed || []).filter((r) => r.__partial);
    if (!partials.length || interval === "day") return null;
    return partials
      .map((r) => `${dayjs(r.date || r.time).format(isMonth ? "MMM" : "MMM D")} is ${r.__bucketDays} of ${r.__calendarDays} days`)
      .join(" · ");
  }, [bucketed, interval, isMonth]);
  // Two bucketing seams, on purpose: `bucketed` is what gets DRAWN (actuals plus
  // any forward forecast layers, needed so the forward stack renders at all), while
  // `bucketedActual` is what gets MEASURED. The forward rows in `chartRows` carry
  // their values on `<group>__forecast` keys, so completeStackedMeta's totalOf()
  // (which only reads stackKeys) would score them as 0 and — since nothing is
  // stamped __partial at day granularity — count them into the completeness
  // sample anyway, diluting the period average and pushing lastTime into forecast
  // dates under a subtitle that reads "Actual ... · <first> – <last>". Bucketing
  // completeRows (actual-only, already tail-trimmed) instead keeps the average and
  // the drawn date range describing only what's actually landed. This also makes
  // __partial MORE honest, not just equivalent to the old behaviour: windowOf
  // clips its coverage window to the last day actually present in the rows it's
  // given, so a month whose actuals stop mid-month is correctly flagged partial.
  const bucketedActual = useMemo(
    () => (isSingleSeries ? [] : bucketStacked(completeRows, bucketKeysForStack, interval, from, to)),
    [isSingleSeries, completeRows, bucketKeysForStack, interval, from, to]);
  const smeta = useMemo(
    () => (isSingleSeries ? null : completeStackedMeta(bucketedActual, stackKeys)),
    [isSingleSeries, bucketedActual, stackKeys]);

  // Cumulative single-series endpoints, for the direct line labels: where the actual
  // line ends is spend-to-date, where the forecast line ends is the projected period
  // total. This equals the "Projected · period end" KPI card by construction, not
  // coincidence: both the chart (clipped to the selected period) and the KPI
  // (BuildPeriodProjection, summed over that same period) are period-scoped rather
  // than rolling-horizon figures — so don't "fix" a future mismatch by reintroducing
  // a rolling sum on either side. Read off the ACCUMULATED rows at the indices meta
  // already found (accumulation preserves order and the null pattern, so the indices
  // still hold).
  const cumEnds = useMemo(() => {
    if (!isCumulative || !isSingleSeries || !meta) return null;
    return {
      actual: meta.lastActualIdx >= 0 ? renderData[meta.lastActualIdx]?.actual : null,
      projected: meta.lastForecastIdx >= 0 ? renderData[meta.lastForecastIdx]?.forecast : null,
    };
  }, [isCumulative, isSingleSeries, meta, renderData]);

  // Cumulative stacked right-edge labels sit at each layer's END-OF-PERIOD total
  // (the whole complete window summed per layer), not at its last single day.
  const cumStackRow = useMemo(() => {
    if (!isCumulative || isSingleSeries) return null;
    const totals = {};
    stackKeys.forEach((k) => { totals[k] = 0; });
    completeRows.forEach((r) => stackKeys.forEach((k) => { totals[k] += Number(r[k]) || 0; }));
    return totals;
  }, [isCumulative, isSingleSeries, completeRows, stackKeys]);
  // The right-edge label anchor must be a BUCKET KEY, not a raw day: at week/month
  // granularity the x-axis domain is bucket keys (Mondays, month-firsts), and a raw
  // day that doesn't land exactly on one makes the categorical scale lookup miss,
  // silently falling back to the full plot width. bucketedActual is already
  // bucket-keyed actual data, so its last row's time IS the last bucket drawn — and
  // at day granularity bucketing is an identity no-op, so this is unchanged there.
  const lastCompleteTime = bucketedActual.length ? bucketedActual[bucketedActual.length - 1].time : null;
  // The subtitle's span, unlike the label anchor above, must be REAL DATES: smeta's
  // firstTime/lastTime are bucket keys, so a stacked Month covering Aug 1 – Sep 30
  // announced itself as "Aug 1 – Sep 1". completeRows is the same data one step
  // earlier — day-level, already tail-trimmed — and is identical to bucketedActual
  // at day granularity, so the day subtitle is untouched.
  const dayFirstTime = completeRows.length ? completeRows[0].time : null;
  const dayLastTime = completeRows.length ? completeRows[completeRows.length - 1].time : null;
  const incompleteCount = hasIncompleteTail ? stackedBase.length - cut : 0;
  const firstIncompleteTime = hasIncompleteTail ? stackedBase[cut].time : null;

  // Drag-to-zoom: track the drag start/current category in STATE so the selection band
  // renders live under the cursor (the visible "handles"); call onZoom on mouseup.
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  // Which stacked layer the cursor is over (bolded in the tooltip).
  const [hoveredKey, setHoveredKey] = useState(null);
  const onMouseDown = (s) => { if (s && s.activeLabel) { setDragStart(s.activeLabel); setDragEnd(null); } };
  const onMouseMove = (s) => { if (dragStart && s && s.activeLabel && s.activeLabel !== dragEnd) setDragEnd(s.activeLabel); };
  // activeLabel is a BUCKET key now, so a drag must resolve to the full calendar
  // span of the dragged buckets — zooming to "2026-09-01".."2026-09-01" would
  // select one day of September instead of the month the user dragged over.
  const onMouseUp = (s) => {
    if (dragStart && s && s.activeLabel && s.activeLabel !== dragStart) {
      const a = dragStart, b = s.activeLabel;
      const lo = a < b ? a : b, hi = a < b ? b : a;
      const hiEnd = bucketEnd(hi, interval);
      onZoom?.(lo, to && hiEnd > to ? to : hiEnd);
    }
    setDragStart(null);
    setDragEnd(null);
  };
  const onMouseLeave = () => { setDragStart(null); setDragEnd(null); setHoveredKey(null); };

  const noForecast = isSingleSeries && !forecast.some((f) => f.forecast != null);
  // A forecast exists, but clipping to the period left too little of it to DRAW.
  // The threshold is "fewer than two surviving points", not "none at all": a
  // one-point <Line dot={false}> renders no segment, so a lone survivor is just as
  // invisible as no survivor — and that is precisely what the four to-date presets
  // (MTD, Last 30 days, QTD, YTD) produce, since they end at today and the fan's
  // first forward day IS today. Without this the chart drew a "Forecast starts"
  // rule, a legend entry and a floating "Projected €X" label over nothing.
  // Distinct from noForecast (no projection exists at all), because the fix is
  // different: widen the period.
  const visibleForecastPoints = useMemo(
    () => (isSingleSeries ? bucketed.filter((r) => r.forecast != null).length : 0),
    [isSingleSeries, bucketed]);
  // The <2-points rule is LINE geometry: a one-point <Line dot={false}> renders no
  // segment. A single BAR draws fine, so Month needs only one surviving point.
  const minVisibleForecast = isMonth ? 1 : 2;
  const forecastBeyondPeriod = isSingleSeries && !noForecast && visibleForecastPoints < minVisibleForecast;
  // Stacked mirror of the same rule: the forward rows were clipped to fewer than two,
  // so the forward layers, the total band and the boundary rule have nothing visible
  // to draw. Previously they just vanished with no explanation at all.
  const stackedForecastBeyondPeriod = !isSingleSeries && rawForwardFrom != null && forwardCount < minVisibleForecast;
  const noVisibleForecast = forecastBeyondPeriod || stackedForecastBeyondPeriod;

  // The Month subtitle states the handover once, in the projected-rest phrasing;
  // `hasForecast` is read off the DRAWN buckets (meta), the date off the day rows.
  const monthHandover = isMonth && !!meta?.hasForecast && !forecastBeyondPeriod && !!dayMeta?.boundary;
  const peakVsAvg = isSingleSeries && meta?.peak && meta?.avg > 0
    ? Math.round((100 * (meta.peak.actual - meta.avg)) / meta.avg)
    : null;
  const stackTitle = title || chartTitleFor({ interval, cumulative: isCumulative, isSingleSeries, groupByLabel });

  return (
    <>
      <div className="mb-2">
        {/* Row 1: title + the two time-series controls. They sit here rather than in
            the page toolbar because they change only this chart, while Breakdown,
            Basis and filters redefine the whole analyzer. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="text-[14px] font-semibold leading-[20px] text-text">{stackTitle}</div>
          <div className="flex items-center gap-4">
            {onIntervalChange && <IntervalSelect value={intervalChoice} onChange={onIntervalChange} />}
            {onCumulativeChange && <CumulativeToggle checked={isCumulative} onChange={onCumulativeChange} />}
          </div>
        </div>
        {/* Subtitle dates are DAY-level (see dayMeta) — never bucket keys. The
            Month-only "actual through X, rest projected" clause SUBSUMES the generic
            "actuals through X", so the two are mutually exclusive: rendering both
            stated the same fact twice in one line. Day and Week keep the original
            pair of clauses verbatim. */}
        {isSingleSeries && dayMeta?.firstDate && (
          <div className="text-[12px] text-text-3">
            {fmtDay(dayMeta.firstDate)} – {fmtDay(dayMeta.lastDate)}
            {dayMeta.boundary && !monthHandover && ` · actuals through ${fmtDay(dayMeta.boundary)}`}
            {meta.hasForecast && !forecastBeyondPeriod && !isMonth && ` · forecast to ${fmtDay(dayMeta.lastDate)}`}
            {monthHandover && ` · actual through ${fmtDay(dayMeta.boundary)}, rest projected`}
            {partialNote && ` · ${partialNote}`}
          </div>
        )}
        {!isSingleSeries && smeta?.firstTime && (
          <div className="text-[12px] text-text-3">
            Actual allocated cloud &amp; AI cost · {fmtDay(dayFirstTime)} – {fmtDay(dayLastTime)} · EUR
            {hasIncompleteTail && ` · ${incompleteCount === 1 ? `${fmtDay(firstIncompleteTime)} still landing (excluded)` : `latest ${incompleteCount} days still landing (excluded)`}`}
            {partialNote && ` · ${partialNote}`}
          </div>
        )}
        {/* Row 3: direct-read line key. Dropped out of the title row so the controls
            have room; still conditional, so it does not render when there is nothing
            to key. The 90% range entry hides at Month, where the band is not drawn
            as a mark — the hatched forecast segment and the tooltip carry it. */}
        {(isSingleSeries || hasComparison || shadow.length > 0) && (
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] text-text-3">
            {isSingleSeries && <span className="flex items-center gap-1.5"><span style={{ width: 16, height: 2, background: CHART }} className="inline-block rounded-full" /> Actual</span>}
            {isSingleSeries && meta?.hasForecast && <span className="flex items-center gap-1.5"><span style={{ width: 16, height: 0, borderTop: `2px dashed ${CHART}` }} className="inline-block" /> Forecast</span>}
            {isSingleSeries && meta?.hasForecast && !isMonth && <span className="flex items-center gap-1.5"><span style={{ width: 14, height: 8, background: BAND, opacity: 0.25 }} className="inline-block rounded-sm" /> 90% range</span>}
            {hasComparison && <span className="flex items-center gap-1.5"><span style={{ width: 16, height: 0, borderTop: `2px dashed ${GHOST}` }} className="inline-block" /> {comparisonLabel}</span>}
            {shadow && shadow.length > 0 && <span className="flex items-center gap-1.5"><span style={{ width: 16, height: 0, borderTop: `2px dashed ${SHADOW}` }} className="inline-block" /> API-equivalent</span>}
          </div>
        )}
      </div>
      <div style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer>
          <ComposedChart data={barData} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseLeave}
            margin={isSingleSeries ? { top: 22, right: 64, bottom: 4, left: 4 } : { top: 24, right: 96, bottom: 4, left: 4 }}
            onContextMenu={(e) => { e.preventDefault(); }}>
            {/* Hatch pattern for the forecast (projected) bar segment. Hatching means
                "projected" and nothing else — a partial bucket is marked with a dashed
                outline instead (see the Cell below), never with this texture. */}
            <defs>
              <pattern id={hatchId} patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
                <rect width={6} height={6} fill={CHART} fillOpacity={0.14} />
                <line x1={0} y1={0} x2={0} y2={6} stroke={CHART} strokeWidth={2.5} strokeOpacity={0.5} />
              </pattern>
            </defs>
            <CartesianGrid stroke="hsl(var(--chart-grid))" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey={xKey} stroke="hsl(var(--chart-axis))" tick={{ fontSize: 11 }}
              tickFormatter={(v) => bucketTickLabel(v, interval, spansYears)} minTickGap={24} />
            <YAxis stroke="hsl(var(--chart-axis))" tick={{ fontSize: 11 }}
              tickFormatter={(v) => humanEUR(v)} width={64} />
            <Tooltip content={<AnalyzerTooltip hoveredKey={hoveredKey} comparisonLabel={hasComparison ? comparisonLabel : null} valueLabel={isSingleSeries ? undefined : valueLabel} cumulative={isCumulative} interval={interval} />} cursor={{ stroke: "hsl(var(--chart-axis))", strokeWidth: 1 }} />
            {/* Live drag-to-zoom selection band (the visible "handles"). */}
            {dragStart && dragEnd && dragStart !== dragEnd && (
              <ReferenceArea x1={dragStart} x2={dragEnd} strokeOpacity={0.3} stroke="hsl(var(--chart-axis))" fill="hsl(var(--chart-axis))" fillOpacity={0.12} isFront />
            )}
            {isSingleSeries ? (
              isMonth ? (
                <>
                  {/* Solid actual. A PARTIAL bucket keeps full fill and takes a dashed
                      outline — hatching is reserved for "projected", and the current
                      month is usually both, so one texture cannot mean two things.
                      Per-bar styling needs <Cell> — a Bar's own stroke props apply to
                      every bar in the series. */}
                  <Bar dataKey="__barActual" stackId="m" fill={CHART} isAnimationActive={false}>
                    {barData.map((r, i) => (
                      <Cell key={r.date || i}
                        stroke={r.__partial ? CHART : "none"}
                        strokeWidth={r.__partial ? 1.5 : 0}
                        strokeDasharray={r.__partial ? "3 2" : undefined} />
                    ))}
                  </Bar>
                  {/* Hatched projection, stacked on top so bar height = projected total. */}
                  <Bar dataKey="__barForecast" stackId="m" fill={`url(#${hatchId})`} stroke={CHART}
                    strokeOpacity={0.5} isAnimationActive={false} />
                </>
              ) : (
                <>
                  {/* Light, borderless forecast-only range band (behind the lines). */}
                  <Area type="linear" dataKey="band" stroke="none" fill={BAND} fillOpacity={0.08} isAnimationActive={false} />
                  {/* Daily-average run-rate reference — daily mode only: a €/day line
                      says nothing about a monotonically rising running total. */}
                  {!isCumulative && meta?.avg != null && (
                    <ReferenceLine y={meta.avg} stroke={REF} strokeDasharray="4 4" strokeOpacity={0.7}
                      label={{ value: `Avg ${humanEUR(meta.avg)}/${intervalNoun(interval)}`, position: "insideTopLeft", fill: "hsl(var(--chart-axis))", fontSize: 11 }} />
                  )}
                  {/* Forecast-starts boundary — label anchored at the BOTTOM so it never
                      collides with the peak annotation at the top when the spike sits near
                      the boundary. No `!isMonth` guard needed here for the same reason as
                      the peak dot below: this element already sits inside the non-month
                      branch. At Month the same fact is stated in the subtitle instead (see
                      "actual through ... rest projected" above) and the stacked mirror of
                      this rule below carries a real `!isMonth` guard, since it is not
                      inside such a branch. */}
                  {meta?.hasForecast && meta?.boundary && (
                    <ReferenceLine x={meta.boundary} stroke={REF} strokeDasharray="3 3" strokeOpacity={0.8}
                      label={{ value: `Forecast starts · ${fmtDay(meta.boundary)}`, position: "insideBottom", fill: "hsl(var(--chart-axis))", fontSize: 11 }} />
                  )}
                  {/* Actual: solid line, no fill, unsmoothed. */}
                  <Line type="linear" dataKey="actual" stroke={CHART} strokeWidth={2.5} dot={false} isAnimationActive={false}
                    label={endLabel(cumEnds?.actual != null ? `Actual ${humanEUR(cumEnds.actual)}` : "Actual", meta?.lastActualIdx, CHART)} />
                  {/* Forecast: dashed line, unsmoothed. */}
                  <Line type="linear" dataKey="forecast" stroke={CHART} strokeWidth={2.5} strokeDasharray="6 4" dot={false} isAnimationActive={false}
                    label={endLabel(cumEnds?.projected != null ? `Projected ${humanEUR(cumEnds.projected)}` : "Forecast", meta?.lastForecastIdx, CHART)} />
                  {/* Peak annotation — daily mode only: the peak of a running total is
                      always its last point, so marking it says nothing. No `!isMonth`
                      guard needed: this whole element already sits inside the non-month
                      branch (see isMonth ? ... : ... above), so isMonth is always false
                      here — the stacked peak dot below, which is NOT inside such a
                      branch, is where that guard actually does something. */}
                  {!isCumulative && meta?.peak && (
                    <ReferenceDot x={meta.peak.date} y={meta.peak.actual} r={3.5} fill={CHART} stroke="hsl(var(--card))" strokeWidth={1.5}
                      label={{ value: peakVsAvg != null ? `Peak ${humanEUR(meta.peak.actual)} (+${peakVsAvg}% vs avg)` : `Peak ${humanEUR(meta.peak.actual)}`, position: "top", fill: "hsl(var(--chart-1))", fontSize: 11, fontWeight: 600 }} />
                  )}
                </>
              )
            ) : (
              <>
                {/* Stacked areas — straight (linear) segments, largest layer at the bottom,
                    strongly muted so it never crushes the smaller layers; the smaller
                    layers get more opacity and a slightly stronger border for contrast. */}
                {/* Month draws Bar instead of Area — a stacked bar per period reads as a
                    period total, matching the single-series switch above. */}
                {stackKeys.map((g, i) => {
                  const color = stackColors[i];
                  const fillOpacity = g === "Other" ? 0.4 : i === 0 ? 0.5 : 0.85;
                  const common = {
                    key: g, dataKey: g, stackId: "a", stroke: color, strokeWidth: i === 0 ? 1 : 1.25,
                    fill: color, fillOpacity, isAnimationActive: false,
                    onMouseEnter: () => setHoveredKey(g), onClick: () => onAddFilter?.(g),
                  };
                  return isMonth ? <Bar {...common} /> : <Area type="linear" {...common} />;
                })}
                {/* Forward (forecast) segments — same colour/order as the actual stack, at a
                    lighter fill so the projected portion reads as provisional. Only present
                    once a forecast breakdown has actually merged in (forwardFrom is set). */}
                {forwardFrom && allStackKeys.map((g, i) => {
                  const common = {
                    key: forecastKey(g), dataKey: forecastKey(g), stackId: "a",
                    stroke: allStackColors[i], strokeWidth: i === 0 ? 1 : 1.25,
                    fill: allStackColors[i], fillOpacity: 0.45, legendType: "none", isAnimationActive: false,
                  };
                  return isMonth ? <Bar {...common} /> : <Area type="linear" {...common} />;
                })}
                {/* Forward uncertainty band, TOTAL only — five overlapping per-segment fans
                    are unreadable, so only the authoritative total gets a range. Never drawn
                    under an active filter: forecast_total_band_daily is a whole-project,
                    dimensionless total, so it carries no valid uncertainty estimate for a
                    filtered subset (mergeForecastRows already omits __band in that case —
                    this mirrors that so nothing tries to fall back to it). At Month the band
                    is not drawn as a mark at all — a range around a bar total has no
                    established reading, and the hatched forecast segment already carries
                    the projected portion. */}
                {forwardFrom && !hasActiveFilters && !isMonth && (
                  <Area type="linear" dataKey="__band" stroke="none" fill={BAND} fillOpacity={0.15} isAnimationActive={false} />
                )}
                {/* Forecast-starts boundary, mirroring the single-series rule below. Guarded
                    against Month, where it would draw inside a bar. */}
                {forwardFrom && !isMonth && (
                  <ReferenceLine x={forwardFrom} stroke={REF} strokeDasharray="3 3"
                    label={{ value: "forecast", position: "top", fill: "hsl(var(--chart-axis))", fontSize: 10 }} />
                )}
                {/* Period-average run-rate reference — lighter so it supports, not
                    competes. Daily mode only (see the single-series note). */}
                {!isCumulative && smeta?.avgTotal != null && (
                  <ReferenceLine y={smeta.avgTotal} stroke={REF} strokeDasharray="4 4" strokeOpacity={0.45}
                    label={{ value: `Period average: ${humanEUR(smeta.avgTotal)}/${intervalNoun(interval)}`, position: "insideTopLeft", fill: "hsl(var(--chart-axis))", fontSize: 11 }} />
                )}
                {/* Thin neutral total line tracing the top of the stack — not drawn across
                    bars, where a line reading "on top of" discrete periods is misleading. */}
                {!isMonth && <Line type="linear" dataKey="__total" stroke={TOTAL_LINE} strokeOpacity={0.5} strokeWidth={1.25} dot={false} isAnimationActive={false} />}
                {/* Peak-total marker (structured text drawn in StackedAnnotations). Not
                    inside a month conditional like the single-series peak dot, so this
                    guard is the one that actually does something — a peak marker
                    floating above a bar adds nothing the bar heights don't already say. */}
                {!isCumulative && smeta?.peak && !isMonth && (
                  <ReferenceDot x={smeta.peak.time} y={smeta.peak.total} r={3.5} fill={TOTAL_LINE} fillOpacity={0.7} stroke="hsl(var(--card))" strokeWidth={1.5} />
                )}
                {/* On-plot text: right-edge Total + short layer labels, and the peak note. */}
                {/* The peak NOTE needs the same `!isMonth` guard as the peak dot above:
                    without it Month rendered "Peak €600 / Sep 1" — a whole month's total
                    labelled with a single day — while the dot it annotates was suppressed. */}
                <Customized component={(p) => <StackedAnnotations {...p} stackKeys={stackKeys} colors={stackColors} lastRow={cumStackRow || smeta?.lastRow} peak={isCumulative || isMonth ? null : smeta?.peak} edgeTime={lastCompleteTime} valueLabel={valueLabel} />} />
              </>
            )}
            {/* Comparison ghost line (prior period / prior year), overlaid on both modes. */}
            {hasComparison && (
              <Line type="linear" dataKey="__comparison" stroke={GHOST} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} />
            )}
            {/* API-equivalent (shadow) reference line, overlaid on both modes. */}
            {shadow && shadow.length > 0 && (
              <Line type="linear" dataKey="__shadow" stroke={SHADOW} strokeWidth={2} strokeDasharray="2 3" dot={false} connectNulls isAnimationActive={false} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {noForecast && (
        <div className="mt-1 text-[12px] text-text-3">No forecast available for this selection</div>
      )}
      {noVisibleForecast && (
        <div className="mt-1 text-[12px] text-text-3">Forecast lies beyond this period — choose a period that ends in the future to see it</div>
      )}
      {!isSingleSeries && (
        <StackedContributorsTable data={completeRows} stackKeys={stackKeys} colors={stackColors} valueLabel={valueLabel} />
      )}
    </>
  );
}

AnalyzerChart.propTypes = {
  isSingleSeries: PropTypes.bool,
  spendDetails: PropTypes.arrayOf(PropTypes.shape({
    time: PropTypes.string,
    grouping: PropTypes.string,
    measure: PropTypes.number,
  })),
  forecast: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    actual: PropTypes.number,
    forecast: PropTypes.number,
    lower: PropTypes.number,
    upper: PropTypes.number,
  })),
  dateGranularity: PropTypes.string,
  onAddFilter: PropTypes.func,
  onZoom: PropTypes.func,
  title: PropTypes.string,
  groupByLabel: PropTypes.string,
  from: PropTypes.string,
  to: PropTypes.string,
  comparison: PropTypes.arrayOf(PropTypes.shape({ time: PropTypes.string, measure: PropTypes.number })),
  comparisonLabel: PropTypes.string,
  valueLabel: PropTypes.func,
  shadow: PropTypes.arrayOf(PropTypes.shape({ time: PropTypes.string, measure: PropTypes.number })),
  forecastBreakdown: PropTypes.shape({
    groups: PropTypes.arrayOf(PropTypes.shape({ day: PropTypes.string, group: PropTypes.string, cost: PropTypes.number })),
    total: PropTypes.arrayOf(PropTypes.shape({ day: PropTypes.string, cost: PropTypes.number, lower: PropTypes.number, upper: PropTypes.number })),
  }),
  hasActiveFilters: PropTypes.bool,
  interval: PropTypes.oneOf(["day", "week", "month"]),
  cumulative: PropTypes.bool,
  intervalChoice: PropTypes.oneOf(["auto", "day", "week", "month"]),
  onIntervalChange: PropTypes.func,
  onCumulativeChange: PropTypes.func,
};
